import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Field, useField } from 'react-final-form';
import PropTypes from 'prop-types';
import { connect, shallowEqual, useDispatch, useSelector } from 'react-redux';
import FfAnimatedInput from '../../../components/final-form/ff-animated-input';
import FfDropdownInput from '../../../components/final-form/ff-dropdown-input2';
import linkedinIco from '../../../assets/icons/linkedin.svg';
import PrCheckbox2 from '../../../components/--primitives/pr-checkbox-2';
import FfLocationInput from '../../../components/final-form/ff-location-input';
import {
  normalizePhone,
  deNormalizePhone,
  validObjectWithParameterKeys,
  strictValidArrayWithLength,
  strictValidArray,
  createDropdownOption,
  strictValidString,
} from '../../../utils/commonUtils';
import PrExpandCollapse from '../../../components/--primitives/pr-expand-collapse';
import FfInputUrl from '../../../components/final-form/ff-input-url';
import FfFileInput from '../../../components/final-form/ff-file-input';
import {
  parseLinkedInUrl,
  formatLinkedInUrl,
} from '../../../utils/validations';
import './module-style.scss';
import { messages } from '../../../language';

import PrDropArea1Component from '../../../components/--primitives/pr-drop-area-1';
import PrTooltipComponent from '../../../components/--primitives/pr-tooltip';
import PrImgIconComponent from '../../../components/--primitives/pr-img-icon';
import icoReload from '../../../assets/icons/reload_small.svg';
import icoDownload from '../../../assets/icons/download_small.svg';
import S3Upload from '../../../components/s3-file-uploader/s3-upload';
import { readCandidateResume } from '../action';

function CandidateInfo({
  form,
  jobFunctions,
  setResumeReading,
  setTempResumePath,
  setResumeData,
  hearAboutUs,
  tenantConfig = {},
}) {
  const dispatch = useDispatch();
  const [area, setArea] = useState([]);
  const [hearUs, setHearUs] = useState([]);
  const [preferredJobLocation, setPreferredJobLocation] = useState(null);
  const formState = (form && form.getState()) || {};
  const { values } = formState;
  const { job_functions = [] } = values;
  const { input, meta } = useField('address');
  const callReadResume = async (UpdateData) => {
    const resumeUrl = new URL(UpdateData.primary_resume);
    const resumePath = resumeUrl.pathname.slice(1);
    const resumeData = await dispatch(
      readCandidateResume({
        file_url: resumePath,
        token: '',
        type: 'user_data',
      }),
    );

    form.change('user.first_name', resumeData.first_name);
    form.change('user.last_name', resumeData.last_name);
    form.change('user.middle_name', resumeData.middle_name);
    form.change('linkedin_url', resumeData.linkedin_url);
    form.change('address', resumeData.prefered_job_location);
    form.change('user.email', resumeData.email);
    form.change('user.phone', resumeData.phone);
    setTempResumePath(resumePath);
    setResumeReading(false);
    setResumeData(resumeData);
  };

  const onUploadSuccess = async (type, val) => {
    let UpdateData = { is_pending: true };
    if (type === 'error') {
      let err;
      try {
        const errorObject = JSON.parse(val);
        err = `${errorObject.message} - ${errorObject.processId}`;
      } catch {
        err = val;
      }
      UpdateData = {
        ...UpdateData,
        upload_resume_error: true,
        s3_error: err,
      };
      setResumeReading(false);
    } else {
      const { data } = val;
      UpdateData = { ...UpdateData, primary_resume: data.split('?')[0] };
      await callReadResume(UpdateData);
    }
  };

  const handleFileFinishedUpload = async (files, onChange) => {
    onChange(files[0]);
    setResumeReading(true);
    new S3Upload({
      // eslint-disable-line
      files,
      signingUrl: `${process.env.REACT_APP_APIHOST}/settings/get-signed-url`,
      s3Path: `temp/candidates/Resume_Documents`,
      onError: async (err) => await onUploadSuccess('error', err),
      onFinishS3Put: async (val) => await onUploadSuccess('', val),
      uploadRequestHeaders: {},
    });
  };
  const resetDropDownValue = (key) => {
    let options = [];
    switch (key) {
      case 'job_functions':
        options = jobFunctions
          .map((v) =>
            values.job_functions.findIndex((val) => val === v._id) < 0
              ? { value: v._id, label: v.title, ...v }
              : false,
          )
          .filter((res) => res.value);
        setArea(options);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const joblist = jobFunctions.filter(
      (v) => job_functions.findIndex((val) => val === v._id) > -1,
    );
    form.mutators.concat('job_functions_list', [
      ...joblist.map((v) => ({ value: v._id, label: v.title, ...v })),
    ]);
    if (strictValidArrayWithLength(jobFunctions))
      resetDropDownValue('job_functions');
  }, [jobFunctions]);

  useEffect(() => {
    if (
      strictValidArray(hearAboutUs) &&
      strictValidArrayWithLength(hearAboutUs)
    ) {
      const val = hearAboutUs.map((v) => {
        if (v.is_default_selected) {
          form.change(`user.source`, { value: v._id, label: v.title, ...v });
        }
        return { value: v._id, label: v.title, ...v };
      });
      setHearUs(val);
    }
  }, [hearAboutUs]);

  useEffect(() => resetDropDownValue('job_functions'), [job_functions]);

  const onSelectOption = (option, key) => {
    let isExist = false;
    if (strictValidArrayWithLength(values[key])) {
      values[key].forEach((item) => {
        if (item.value && item.value.indexOf(option.value) > -1) isExist = true;
      });
    }
    if (!isExist) {
      form.mutators.push(key, option.value);
      form.mutators.push(`${key}_list`, option);
    } else {
      form.change(key, []);
      form.mutators.push(key, option.value);
      form.mutators.push(`${key}_list`, option);
    }
  };

  const onRemoveOption = (key, index) => {
    form.change(`${key}_select`, '');
    form.mutators.remove(key, index);
    form.mutators.remove(`${key}_list`, index);
  };

  return (
    <div className="content-aligner candidate-info">
      <div className="title-block">
        <div className="title">
          <h2>Candidate Information</h2>
        </div>
        <p>
          {messages.APPLY_PROCESS_HELPER_TEXT.CANDIDATE_INFO(tenantConfig.name)}
        </p>
      </div>

      <div className="row vertical">
        <PrExpandCollapse title="RESUME" className="type-3">
          <Field
            name="primary_resume"
            component={({ meta, input: { onChange, value } }) => {
              return [
                <FfFileInput
                  key="primary_resume"
                  showErrorModal
                  onFileUpload={async (data) => {
                    handleFileFinishedUpload(data, onChange);
                  }}
                  toShowContent={(dropZone) => (
                    <PrDropArea1Component
                      title="Primary Resume"
                      subtitle={value && value.name}
                      type={`primary ${
                        value && value && value.name ? 'filled' : ''
                      }`}
                      // eslint-disable-next-line react/no-unstable-nested-components
                      tooltip={(active) => (
                        <PrTooltipComponent
                          type={`top-right shift with-triangle with-extra-cta ${
                            active ? 'in-view' : ''
                          }`}
                          CTAsList={[
                            {
                              ico: (
                                <PrImgIconComponent
                                  type="default"
                                  url={icoReload}
                                />
                              ),
                              name: 'Update',
                              cursor: true,
                              onClick: () =>
                                dropZone.current && dropZone.current.open(),
                            },
                            {
                              ico: (
                                <PrImgIconComponent
                                  type="default"
                                  url={icoDownload}
                                />
                              ),
                              name: 'Download',
                              cursor: true,
                              onClick: () => {
                                if (
                                  !validObjectWithParameterKeys(value, [
                                    'path',
                                    'name',
                                  ])
                                )
                                  return;
                              },
                            },
                          ]}
                        />
                      )}
                    />
                  )}
                  parallelUploads={1}
                  uploadMultiple={false}
                  allowedMimeTypes={[
                    '.pdf',
                    '.doc',
                    '.docx',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  ]}
                />,
                meta.touched && meta.error && (
                  <span className="text text-danger">{meta.error}</span>
                ),
              ];
            }}
          />
          {/* <Field
            name="resume"
            // component={(value, onChange) => <input type="file" />}
            component={FfFileInput}
            className="full"
          /> */}
        </PrExpandCollapse>
        <PrExpandCollapse title="Main Info" className="type-3">
          <ul className="gallery column-2">
            <li>
              <Field
                name="user.first_name"
                placeholder="First Name"
                component={FfAnimatedInput}
              />
            </li>
            <li>
              <Field
                name="user.middle_name"
                placeholder="Middle Name"
                component={FfAnimatedInput}
              />
            </li>
            <li>
              <Field
                name="user.last_name"
                placeholder="Last Name"
                component={FfAnimatedInput}
              />
            </li>
            <li>
              <Field
                name="user.source"
                component={FfDropdownInput}
                options={createDropdownOption({ arr: hearUs, status: true })}
                placeholder="How did you hear about Us?"
                onSelect={() => form.change('user.source_text', '')}
              />
            </li>
            {validObjectWithParameterKeys(values, ['user']) &&
              validObjectWithParameterKeys(values.user, ['source']) &&
              values.user.source.show_resource_field && (
                <li>
                  <Field
                    name="user.source_text"
                    component={FfAnimatedInput}
                    placeholder="Source"
                  />
                </li>
              )}

            <li>
              <Field
                name="user.phone"
                component={FfAnimatedInput}
                format={normalizePhone}
                parse={deNormalizePhone}
                placeholder="Phone"
              />
            </li>
            <li>
              <Field
                name="user.email"
                placeholder="Email"
                component={FfAnimatedInput}
                parse={(email) => email && email.toLowerCase()}
              />
            </li>
          </ul>
        </PrExpandCollapse>

        <PrExpandCollapse title="Linkedin Profile" className="type-3">
          <ul className="gallery job-location-info">
            <li>
              <Field
                name="linkedin_url"
                component={FfInputUrl}
                ico={linkedinIco}
                className="full"
                urlPlaceholder="https://www.linkedin.com/in/"
                showHyperLink
                parse={parseLinkedInUrl}
                format={formatLinkedInUrl}
              />
            </li>
          </ul>
        </PrExpandCollapse>

        <PrExpandCollapse title="Preferred Job Location" className="type-3">
          <ul className="gallery column-2">
            <li>
              <Field
                name="address"
                component={FfLocationInput}
                canBeEmpty
                searchType="regions"
                placeholder="City or Zipcode"
                filtersLocation={['locality', 'postal_code', 'sublocality']}
              />
            </li>
          </ul>
        </PrExpandCollapse>

        <PrExpandCollapse title="Job Interests" className="type-3">
          <ul className="gallery column-2 ">
            <li>
              <Field
                name="job_functions_select"
                component={FfDropdownInput}
                placeholder="WHAT JOB FUNCTIONS INTEREST YOU?"
                options={createDropdownOption({ arr: area, status: true })}
                onSelect={(option) => {
                  onSelectOption(option, 'job_functions');
                }}
                type="select"
                clearField
              />
            </li>
            <li>
              <div className="options-display">
                {strictValidArrayWithLength(values.job_functions_list) &&
                  values.job_functions_list.map((val = {}, i) => {
                    const { subtitle, label } = val;
                    const subtitleLable = `${label} - ${subtitle}`;
                    const labelShown =
                      val && val.label && `${subtitle ? subtitleLable : label}`;
                    return (
                      <div
                        key={val && val.value}
                        style={{
                          marginRight: '10px',
                          marginBottom: '10px',
                          maxWidth: '100%',
                        }}
                      >
                        <PrCheckbox2
                          key={val && val.value}
                          config={{
                            selected: true,
                            value: labelShown,
                          }}
                          onClick={() => {
                            onRemoveOption('job_functions', i);
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </li>
          </ul>
        </PrExpandCollapse>
      </div>
    </div>
  );
}
CandidateInfo.propTypes = {
  form: PropTypes.shape(PropTypes.object),
  jobFunctions: PropTypes.shape(PropTypes.array),
  hearAboutUs: PropTypes.shape(PropTypes.array),
  tenantConfig: PropTypes.shape(PropTypes.object),
};

CandidateInfo.defaultProps = {
  form: null,
  jobFunctions: [],
  hearAboutUs: [],
  tenantConfig: {},
};
const mapStateProps = (state) => {
  return {
    user: state.get('auth').get('user'),
    jobFunctions: state.get('settings').get('jobFunctions'),
    hearAboutUs: state.get('settings').get('candidate_hear_about_us'),
    tenantConfig: state.get('config').get('tenantConfig'),
  };
};

export default connect(mapStateProps, null)(CandidateInfo);
